import { Database, Server, Activity, Wifi, Clock } from 'lucide-react';
import { formatUptime } from '../utils/productAnalytics';

const tileClass = (status) => {
  if (status === 'up' || status === 'ok') {
    return 'bg-white border-green-200 text-green-800';
  }
  if (status === 'disabled') {
    return 'bg-white border-gray-200 text-gray-700';
  }
  return 'bg-white border-red-200 text-red-800';
};

const labelStatus = (status) => {
  if (status === 'up' || status === 'ok') return 'UP';
  if (status === 'disabled') return 'Off';
  if (status === 'down' || status === 'error') return 'DOWN';
  return status || '—';
};

export function getOverallHealthStatus(health) {
  if (!health) return { level: 'unknown', label: 'Indisponible' };
  if (health.status !== 'ok') {
    return { level: 'degraded', label: 'Plateforme dégradée' };
  }
  const db = health.services?.db?.status;
  const redis = health.services?.redis?.status;
  if (db === 'down') {
    return { level: 'degraded', label: 'Base de données indisponible' };
  }
  if (redis === 'down' || redis === 'error') {
    return { level: 'degraded', label: 'Cache Redis en erreur' };
  }
  return { level: 'ok', label: 'Tous les services sont opérationnels' };
}

/**
 * Tuiles santé — Dashboard (compact) et Analytics (détail).
 */
export default function SystemHealthCards({
  health,
  clientLatency,
  compact = false,
  checkedAt,
}) {
  if (!health) {
    return (
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        Impossible de joindre l&apos;API santé (<code className="text-xs">/api/health</code>).
      </p>
    );
  }

  const overall = getOverallHealthStatus(health);
  const padding = compact ? 'p-3' : 'p-4';
  const titleSize = compact ? 'text-base' : 'text-lg';

  return (
    <div className="space-y-3">
      <div
        className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 ${
          overall.level === 'ok'
            ? 'bg-green-100 border-green-300 text-green-900'
            : overall.level === 'degraded'
              ? 'bg-red-100 border-red-300 text-red-900'
              : 'bg-gray-100 border-gray-300 text-gray-800'
        }`}
        role="status"
        aria-live="polite"
      >
        <span className={`font-semibold ${compact ? 'text-sm' : 'text-base'}`}>
          {overall.level === 'ok' ? '● ' : overall.level === 'degraded' ? '● ' : '○ '}
          {overall.label}
        </span>
        {checkedAt && (
          <span className="text-xs opacity-80">
            Vérifié à {checkedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
      </div>

      <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3`}>
        <div className={`rounded-lg border ${padding} ${tileClass(health.status)}`}>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-80">
            <Activity className="h-3.5 w-3.5" />
            API
          </div>
          <div className={`${titleSize} font-bold mt-1`}>
            {health.status === 'ok' ? 'UP' : 'DOWN'}
          </div>
        </div>

        <div className={`rounded-lg border ${padding} ${tileClass(health.services?.db?.status)}`}>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-80">
            <Database className="h-3.5 w-3.5" />
            PostgreSQL
          </div>
          <div className={`${titleSize} font-bold mt-1 capitalize`}>
            {labelStatus(health.services?.db?.status)}
          </div>
        </div>

        <div className={`rounded-lg border ${padding} ${tileClass(health.services?.redis?.status)}`}>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-80">
            <Server className="h-3.5 w-3.5" />
            Redis
          </div>
          <div className={`${titleSize} font-bold mt-1 capitalize`}>
            {labelStatus(health.services?.redis?.status)}
          </div>
        </div>

        <div className={`rounded-lg border ${padding} bg-white border-gray-200 text-gray-900`}>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <Wifi className="h-3.5 w-3.5" />
            Latence
          </div>
          <div className={`${titleSize} font-bold mt-1`}>
            {clientLatency != null ? `${clientLatency} ms` : '—'}
          </div>
        </div>

        <div className={`rounded-lg border ${padding} bg-white border-gray-200 text-gray-900 col-span-2 sm:col-span-1`}>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <Clock className="h-3.5 w-3.5" />
            Uptime API
          </div>
          <div className={`${titleSize} font-bold mt-1`}>
            {health.uptimeHuman || formatUptime(health.uptimeSeconds)}
          </div>
          {health.startedAt && !compact && (
            <div className="text-xs text-gray-500 mt-0.5 truncate" title={health.startedAt}>
              depuis {new Date(health.startedAt).toLocaleString('fr-FR')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
